// Types
import type { IFinding, IFindingStatus } from '@/types/appsec/finding'
import type { ISeverity } from '@/types/severity'

// Components
import { Autocomplete, Box, Typography } from '@/CommonComponents'
import type { IOption } from '@/CommonComponents/Autocomplete'
// Hooks
import { useMemo } from 'react'
// Constants
import { getOccurrences } from './utils'
import { useTexts } from '@/contexts/LanguageContext'

interface IRepositoryFilterProps<T extends IFinding> {
  filterCriteria: {
    status: IFindingStatus | null
    severity: ISeverity | null
    repository: string | null
  }
  findings: T[]
  onChange: (repository: string | null) => void
}
function getUniqueRepositories(findings: IFinding[]) {
  return [...new Set(findings.map((f) => f.repository).filter((r) => r))]
}

export default function RepositoryFilter<T extends IFinding>({
  filterCriteria,
  findings,
  onChange,
}: IRepositoryFilterProps<T>) {
  const { APPSEC } = useTexts()
  const availableRepositories = getUniqueRepositories(findings)

  const options: IOption[] = useMemo(() => {
    return availableRepositories.map((repository) => {
      const count = getOccurrences({
        findings,
        severity: filterCriteria.severity,
        status: filterCriteria.status,
        repository,
      })
      return {
        id: repository || '',
        label: `${repository} (${count})`,
      }
    })
  }, [availableRepositories, findings, filterCriteria.severity, filterCriteria.status])

  if (availableRepositories.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
      <Autocomplete
        options={options}
        values={filterCriteria.repository || ''}
        placeholder={APPSEC.SCA.TABLE.REPOSITORY}
        onChange={(value) => {
          const selected = Array.isArray(value) ? value[0] : value
          onChange(selected || null)
        }}
        size="small"
        sx={{ minWidth: 300 }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string }
          return (
            <li key={key} {...rest}>
              <Typography variant="body2">{option.label}</Typography>
            </li>
          )
        }}
      />
    </Box>
  )
}
